import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";
import axios from "axios";

/**
 * Course Master Report
 * - List endpoint (assumed): GET /api/course-master/:clientId/:collegeId
 * - CSV: GET /api/course-master/:clientId/:collegeId/download/csv
 * - PDF: GET /api/course-master/:clientId/:collegeId/download/pdf
 *
 * Note: If your server uses different endpoint names, adjust the URLs below.
 */
export default function CourseMasterReport() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [regulations, setRegulations] = useState([]);

  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedRegulation, setSelectedRegulation] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedWorkflowStatuses, setSelectedWorkflowStatuses] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const workflowStatuses = ["DRAFT", "APPROVED", "REJECTED"];

  const user = useMemo(() => {
    const userData = sessionStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchPrograms = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );
      if (Array.isArray(resp.data)) setPrograms(resp.data);
      else if (resp.data && Array.isArray(resp.data.data)) setPrograms(resp.data.data);
      else setPrograms([]);
    } catch (err) {
      console.error("Error fetching programs", err);
      setPrograms([]);
    }
  }, [user]);

  const fetchBranches = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/branches/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );
      if (resp.data && resp.data.data && Array.isArray(resp.data.data)) setBranches(resp.data.data);
      else setBranches([]);
    } catch (err) {
      console.error("Error fetching branches", err);
      setBranches([]);
    }
  }, [user]);

  const fetchRegulations = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/regulations/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );
      // regulations endpoint may return { items, total } or { data } or raw array
      if (Array.isArray(resp.data)) setRegulations(resp.data);
      else if (resp.data && Array.isArray(resp.data.data)) setRegulations(resp.data.data);
      else if (resp.data && Array.isArray(resp.data.items)) setRegulations(resp.data.items);
      else setRegulations([]);
    } catch (err) {
      console.error("Error fetching regulations", err);
      setRegulations([]);
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search && search.trim() !== "") params.append("search", search.trim());
      // Use backend-expected parameter names (snake_case) so repository filters match
      if (selectedProgram) params.append("program_id", selectedProgram);
      if (selectedBranch) params.append("branch_id", selectedBranch);
      if (selectedRegulation) params.append("regulation_id", selectedRegulation);
      if (selectedYear) params.append("year_number", selectedYear);
      if (selectedSemester) params.append("semester_number", selectedSemester);
      if (selectedWorkflowStatuses && selectedWorkflowStatuses.length > 0)
        params.append("workflow_status", selectedWorkflowStatuses.join(","));

      const queryString = params.toString();

      // use the new Course Master backend endpoint
      const baseUrl = `${API_BASE_URL}/api/course-master/${user.client_id}/${user.college_id}`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const resp = await axios.get(url, { headers: getAuthHeaders() });
      let data = [];
      if (Array.isArray(resp.data)) data = resp.data;
      else if (resp.data && Array.isArray(resp.data.data)) data = resp.data.data;

      // prefer exam_course_id (serialized string) as the id when available
      const formatted = data.map((r, i) => ({
        id: r.exam_course_id || r.course_id || r.id || i,
        ...r,
      }));
      setRows(formatted);
    } catch (err) {
      console.error("Error fetching course master data", err);
      showSnackbar("Error fetching data", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    user,
    search,
    selectedProgram,
    selectedBranch,
    selectedRegulation,
    selectedYear,
    selectedSemester,
    selectedWorkflowStatuses,
  ]);

  const handleDownloadCsv = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      // backend-expected params
      if (selectedProgram) params.append("program_id", selectedProgram);
      if (selectedBranch) params.append("branch_id", selectedBranch);
      if (selectedRegulation) params.append("regulation_id", selectedRegulation);
      if (selectedYear) params.append("year_number", selectedYear);
      if (selectedSemester) params.append("semester_number", selectedSemester);
      if (selectedWorkflowStatuses && selectedWorkflowStatuses.length > 0)
        params.append("workflow_status", selectedWorkflowStatuses.join(","));

      const queryString = params.toString();
      // Prefer server-generated CSV endpoint; fallback to client-side if server fails
      const csvUrl =
        `${API_BASE_URL}/api/course-master/${user.client_id}/${user.college_id}/download/csv` +
        (queryString ? `?${queryString}` : "");
      try {
        const resp = await axios.get(csvUrl, { headers: getAuthHeaders(), responseType: "blob" });
        const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8;" });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", "course_master_full.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        showSnackbar("CSV downloaded successfully", "success");
        return;
      } catch (err) {
        console.warn("Server CSV failed, falling back to client CSV", err);
      }

      // Fallback: client-side CSV from currently loaded rows
      const csvHeaders = [
        "exam_course_id",
        "course_code",
        "short_code",
        "course_name",
        "course_type",
        "course_category",
        "credits",
        "college_id",
      ];
      const csvData = rows.map((r) => [
        r.exam_course_id || r.course_id || "",
        r.course_code || "",
        r.short_code || "",
        r.course_name || "",
        r.course_type || "",
        r.course_category || "",
        r.credits || "",
        r.college_id || "",
      ]);
      const csvContent = [
        csvHeaders.join(","),
        ...csvData.map((row) => row.map((f) => `"${String(f).replace(/"/g, '""')}"`).join(",")),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "course_master.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("CSV downloaded successfully (client-side)", "success");
    } catch (err) {
      console.error("Error downloading CSV", err);
      showSnackbar("Error downloading CSV", "error");
    }
  };

  const handleDownloadPdf = async () => {
    if (!user) return;
    try {
      const params = new URLSearchParams();
      // backend-expected params
      if (selectedProgram) params.append("program_id", selectedProgram);
      if (selectedBranch) params.append("branch_id", selectedBranch);
      if (selectedRegulation) params.append("regulation_id", selectedRegulation);
      if (selectedYear) params.append("year_number", selectedYear);
      if (selectedSemester) params.append("semester_number", selectedSemester);
      if (selectedWorkflowStatuses && selectedWorkflowStatuses.length > 0)
        params.append("workflow_status", selectedWorkflowStatuses.join(","));

      const queryString = params.toString();
      // Call server PDF endpoint if available
      const pdfUrl =
        `${API_BASE_URL}/api/course-master/${user.client_id}/${user.college_id}/download/pdf` +
        (queryString ? `?${queryString}` : "");
      try {
        const resp = await axios.get(pdfUrl, { headers: getAuthHeaders(), responseType: "blob" });
        const blob = new Blob([resp.data], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", "course_master_compact.pdf");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        showSnackbar("PDF downloaded successfully", "success");
        return;
      } catch (err) {
        console.warn("Server PDF failed or not available", err);
        showSnackbar("PDF export not available. Please use CSV export.", "info");
      }
    } catch (err) {
      console.error("Error downloading PDF", err);
      showSnackbar("Error downloading PDF", "error");
    }
  };

  useEffect(() => {
    if (user) {
      fetchPrograms();
      fetchBranches();
      fetchRegulations();
      fetchData();
    }
  }, [user, fetchPrograms, fetchBranches, fetchRegulations, fetchData]);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleFilterClick = (e) => setFilterAnchorEl(e.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const toggleWorkflowStatus = (s) =>
    setSelectedWorkflowStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );

  const clearFilters = () => {
    setSelectedProgram("");
    setSelectedBranch("");
    setSelectedRegulation("");
    setSelectedYear("");
    setSelectedSemester("");
    setSelectedWorkflowStatuses([]);
  };

  const columns = [
    {
      field: "program_name",
      headerName: "Program",
      flex: 1.2,
      minWidth: 160,
      valueGetter: (p) => p.row.program_name || p.row.program?.program_name || "",
    },
    {
      field: "branch_name",
      headerName: "Branch",
      flex: 1.2,
      minWidth: 160,
      valueGetter: (p) => p.row.branch_name || p.row.branch?.branch_name || "",
    },
    {
      field: "regulation_name",
      headerName: "Regulation",
      flex: 0.8,
      minWidth: 110,
      valueGetter: (p) =>
        p.row.regulation_name || p.row.regulation?.regulation_name || p.row.regulation_id || "",
    },
    {
      field: "year_number",
      headerName: "Year",
      flex: 0.6,
      minWidth: 90,
      valueGetter: (p) => p.row.year_number || p.row.year || "",
    },
    {
      field: "semester_number",
      headerName: "Semester",
      flex: 0.6,
      minWidth: 90,
      valueGetter: (p) => p.row.semester_number || p.row.semester || "",
    },
    { field: "course_code", headerName: "Course Code", flex: 0.8, minWidth: 120 },
    { field: "short_code", headerName: "Short Code", flex: 0.7, minWidth: 110 },
    { field: "course_name", headerName: "Course Name", flex: 1.6, minWidth: 200 },
    { field: "course_level", headerName: "Course Level", flex: 0.8, minWidth: 120 },
    { field: "course_type", headerName: "Course Type", flex: 0.8, minWidth: 120 },
    { field: "credits", headerName: "Credits", flex: 0.5, minWidth: 90 },
    { field: "workflow_status", headerName: "Workflow Status", flex: 0.9, minWidth: 120 },
    { field: "status", headerName: "Status", flex: 0.7, minWidth: 100 },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            COURSE MASTER REPORT
          </Typography>

          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            onClick={handleMenuOpen}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            ACTIONS
          </Button>

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#115293", color: "#115293" },
            }}
          >
            FILTERS
            {(selectedProgram ||
              selectedBranch ||
              selectedRegulation ||
              selectedYear ||
              selectedSemester ||
              selectedWorkflowStatuses.length) && (
              <Chip
                size="small"
                label={
                  (selectedProgram ? 1 : 0) +
                  (selectedBranch ? 1 : 0) +
                  (selectedRegulation ? 1 : 0) +
                  (selectedYear ? 1 : 0) +
                  (selectedSemester ? 1 : 0) +
                  (selectedWorkflowStatuses ? selectedWorkflowStatuses.length : 0)
                }
                sx={{ ml: 1, backgroundColor: "#1976d2", color: "white" }}
              />
            )}
          </Button>

          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{ backgroundColor: "#4caf50", color: "#fff" }}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadCsv();
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                sx={{ backgroundColor: "#e53935", color: "#fff" }}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadPdf();
                }}
              >
                DOWNLOAD PDF
              </Button>
            </MenuItem>
          </Menu>

          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                background: "#ffffff",
                minWidth: 240,
                maxWidth: 360,
              },
            }}
          >
            <Box sx={{ p: 1.5, background: "#ffffff" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ color: "#1976d2", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Filters
                </Typography>
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                    p: 0.5,
                    "&:hover": { backgroundColor: "#ffebee" },
                  }}
                >
                  Clear
                </Button>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Program
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedProgram || ""}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Programs --</option>
                  {programs.map((p) => (
                    <option key={p.program_id || p.id} value={p.program_id || p.id}>
                      {p.program_name || p.name}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Branch
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedBranch || ""}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Branches --</option>
                  {branches.map((b) => (
                    <option key={b.branch_id} value={b.branch_id}>
                      {b.branch_name}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Regulation
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedRegulation || ""}
                  onChange={(e) => setSelectedRegulation(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Regulations --</option>
                  {regulations.map((r) => (
                    <option key={r.regulation_id || r.id} value={r.regulation_id || r.id}>
                      {r.regulation_name || r.name}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

              <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 0.5,
                      color: "#666",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      display: "block",
                    }}
                  >
                    Year
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. 1"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      mb: 0.5,
                      color: "#666",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      display: "block",
                    }}
                  >
                    Semester
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. 1"
                    value={selectedSemester}
                    onChange={(e) => setSelectedSemester(e.target.value)}
                  />
                </Box>
              </Box>

              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Workflow Status
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: 0.5,
                    border: "1px solid #e9ecef",
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  <List dense sx={{ py: 0 }}>
                    {workflowStatuses.map((s) => (
                      <ListItem key={s} disablePadding>
                        <ListItemButton
                          onClick={() => toggleWorkflowStatus(s)}
                          sx={{ py: 0.25, px: 0.5, minHeight: 28 }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedWorkflowStatuses.includes(s)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                            sx={{ color: "#1976d2", p: 0.25 }}
                          />
                          <ListItemText
                            primary={s}
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.8rem",
                                color: "#555",
                                fontWeight: selectedWorkflowStatuses.includes(s) ? 600 : 400,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Box>
          </Popover>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={fetchData}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
